import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import Typography from '@mui/material/Typography';
import SubmitButton from 'components/Submits/SubmitButton';
import Flex from 'ui/Flex/Flex';
import { translateBlock } from 'i18n';
import {
    useGetClientMapping,
    useResetClientMapping,
    useImportClientMapping,
} from 'routes/Support/hooks/mappings';
import { capitalize, getTableName } from 'helpers/helpers';
import { Autocomplete, List, ListItem, TextField } from '@mui/material';
import SubmitMappingModal from './SubmitMappingModal';

type TransfertMappingsProps = { selectedBind: string };
type GroupedCategories = {
    value: string;
    label: string;
    group: string;
};

const TransfertMappings = ({ selectedBind }: TransfertMappingsProps) => {
    const { t } = useTranslation();

    const [selectedCategory, setSelectedCategory] =
        useState<GroupedCategories | null>(null);

    const { data: rawCategories } = useGetClientMapping(selectedBind);
    const resetMappingMutation = useResetClientMapping(selectedBind);
    const importMappingMutation = useImportClientMapping(selectedBind);
    const categories: string[] = rawCategories || [];

    const groupdedCategories = categories.map((category) => ({
        value: category,
        label: capitalize(translateBlock(category)),
        group: capitalize(translateBlock(getTableName(category))),
    }));

    const submitTransfert = () => {
        if (!selectedBind || !selectedCategory) {
            console.error('bind or category not defined where it should');
            return;
        }
        importMappingMutation.mutate(selectedCategory.value);
    };
    const submitReset = () => {
        if (!selectedBind || !selectedCategory) {
            console.error('bind or category not defined where it should');
            return;
        }
        resetMappingMutation.mutate(selectedCategory.value);
    };

    return (
        <Flex direction="column" margin="1rem">
            <Typography variant="h5">
                {t('support.transferMappings.title')}
            </Typography>
            <Flex direction="column" mb="1rem">
                <Typography variant="subtitle1">
                    {t('support.transferMappings.explanation.subtitle')}
                </Typography>
                <List sx={{ p: 0, listStyleType: 'disc', ml: '1rem' }}>
                    <ListItem sx={{ p: 0, display: 'list-item' }}>
                        <Typography variant="subtitle1">
                            {t(
                                'support.transferMappings.explanation.emptyMappings',
                            )}
                        </Typography>
                    </ListItem>
                    <ListItem sx={{ p: 0, display: 'list-item' }}>
                        <Typography variant="subtitle1">
                            {t(
                                'support.transferMappings.explanation.importMappings',
                            )}
                        </Typography>
                    </ListItem>
                </List>
            </Flex>
            <Flex>
                <Flex
                    direction="column"
                    flexGrow={1}
                    maxWidth="500px"
                    paddingRight="4rem"
                >
                    <Autocomplete
                        options={groupdedCategories}
                        groupBy={(option) => option.group}
                        value={selectedCategory}
                        onChange={(event, value) => setSelectedCategory(value)}
                        disabled={!categories.length}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="standard"
                                label={t(
                                    'support.transferMappings.selectMappingCategory',
                                )}
                            />
                        )}
                    />
                </Flex>
                <SubmitButton
                    sx={{ mr: '2rem' }}
                    submit={submitReset}
                    buttonLabel={t('support.transferMappings.resetMappings')}
                    dialogTitle={t('support.transferMappings.resetMappings')}
                    disabled={
                        selectedCategory === null || selectedBind === null
                    }
                >
                    <SubmitMappingModal
                        title="support.transferMappings.submitModal.emptyMappingModal"
                        selectedCategory={selectedCategory?.label}
                    />
                </SubmitButton>
                <SubmitButton
                    submit={submitTransfert}
                    buttonLabel={t('support.transferMappings.transferMappings')}
                    dialogTitle={t('support.transferMappings.transferMappings')}
                    disabled={
                        selectedCategory === null || selectedBind === null
                    }
                >
                    <SubmitMappingModal
                        title="support.transferMappings.submitModal.importMappingModal"
                        selectedCategory={selectedCategory?.label}
                    />
                </SubmitButton>
            </Flex>
        </Flex>
    );
};

export default TransfertMappings;
